import { Slide, type SlideProps, Typography, useMediaQuery, useTheme } from "@mui/material";
import { type ReactNode, forwardRef } from "react";
import { useSelector } from "react-redux";

import FeaturesList from "@/Dashboard/pages/PropertySettings/components/PropertyInfoPanel/FeaturesList";
import SegmentsList from "@/Dashboard/pages/PropertySettings/components/PropertyInfoPanel/SegmentsList";
import SocialMediaList from "@/Dashboard/pages/PropertySettings/components/PropertyInfoPanel/SocialMediaList";
import { setSimpleValue } from "@/store/actions";
import { useThunkDispatch } from "@/store/hooks";
import { currentPropertySelector, simpleValuesSelector } from "@/store/selectors";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import { getMediaLink } from "@/utils/mediaLink";

import PropertyMap from "./PropertyMap";
import PropertyPhoto from "./PropertyPhoto";
import * as S from "./styled";

const Transition = forwardRef(({ children, ...props }: { children: ReactNode } & SlideProps, ref) => (
  <Slide direction="down" ref={ref} {...props}>
    {children}
  </Slide>
));

export default function PropertyInfoCard() {
  const dispatch = useThunkDispatch();
  const simpleValues = useSelector(simpleValuesSelector);
  const { property } = useSelector(currentPropertySelector);

  const { splashPageLogo, phone, email, website, features, segments, socialMedia, address, description, photo } =
    property || {};

  const logoUrl = getMediaLink({ link: splashPageLogo });
  const shareKey = "property_info_card";
  const isDialogOpen = !!simpleValues?.[shareKey];

  const onCloseDialog = () => {
    dispatch(setSimpleValue(shareKey, undefined));
  };
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.between("xs", "sm"));
  const hasContactInfo = phone?.number || email || website;
  const hasBrandingFeatures = (features || []).length > 0;
  const hasBrandingSegments = (segments || []).length > 0;
  const hasSocialMedia = (socialMedia || []).length > 0;

  return (
    <S.ModalDialog
      open={isDialogOpen}
      onClose={onCloseDialog}
      aria-labelledby="form-dialog-title"
      TransitionComponent={Transition}
      maxWidth={photo && address ? "lg" : "md"}
      data-testid="property-info-dialog"
    >
      <S.ModalTitle>
        {logoUrl && <S.PropertyLogo src={logoUrl} alt="logo" />}
        <S.ModalHeader>
          <S.Title>
            <S.PropertyName>{property.name}</S.PropertyName>
            <S.CloseIcon onClick={onCloseDialog} data-testid="close-icon" />
          </S.Title>
          <S.Divider />
          <S.PropertyAddress>{address || ""}</S.PropertyAddress>
        </S.ModalHeader>
      </S.ModalTitle>

      <S.ModalContent>
        <S.PropertyDescription>{description || ""}</S.PropertyDescription>
        <S.PropertyDetailsWrapper>
          <PropertyPhoto photoUrl={photo} />
          <PropertyMap address={address || ""} />
          <S.BrandingLists>
            {hasBrandingFeatures && (
              <S.ListColumn data-testid="features">
                <Typography sx={{ fontSize: "16px", color: "#fff", fontWeight: "700" }}>Features</Typography>
                <FeaturesList isPropertyInfoCard={true} features={features || []} />
              </S.ListColumn>
            )}
            {hasBrandingSegments && (
              <S.ListColumn data-testid="segments">
                <Typography sx={{ fontSize: "16px", color: "#fff", fontWeight: "700" }}>Segments</Typography>
                <SegmentsList isPropertyInfoCard={true} segments={segments || []} />
              </S.ListColumn>
            )}
          </S.BrandingLists>
        </S.PropertyDetailsWrapper>
      </S.ModalContent>

      {hasContactInfo && (
        <S.ModalContent>
          <S.CommonWrapper>
            <S.ContactInfo href={`tel:${formatPhoneNumber(phone?.number)}`} $hasLink={!!phone?.number}>
              {isMobile && phone?.number ? "Phone" : formatPhoneNumber(phone?.number)}
            </S.ContactInfo>
            <S.ContactInfo href={`mailto:${email}`} $hasLink={!!email}>
              {isMobile && email ? "Email" : email}
            </S.ContactInfo>
            <S.ContactInfo href={website} target="_blank" $hasLink={!!website}>
              {isMobile && website ? "Website" : website}
            </S.ContactInfo>
          </S.CommonWrapper>
        </S.ModalContent>
      )}
      {hasSocialMedia && (
        <S.ModalContent data-testid="social-media">
          <S.CommonWrapper>
            <SocialMediaList isPropertyInfoCard={true} socialMedia={socialMedia || []} />
          </S.CommonWrapper>
        </S.ModalContent>
      )}
    </S.ModalDialog>
  );
}
